/* ============================================================
   AM2T — global-header.js
   Loaded by sections/global-header.liquid (every page).
   1. Header follows the promo bar up while it scrolls away, then
      stays fixed at top: 0.
   2. Overlay header (home): turns solid when the hero is covered.
   3. Desktop "Shop" dropdown (hover / focus / Escape).
   4. Mobile menu: Shop submenu toggle.
   One passive scroll listener per behaviour that needs it; all work
   is batched in requestAnimationFrame.
   ============================================================ */

(() => {
  'use strict';

  /* === 1. Header offset under the promo bar ====================== */
  const initOffset = () => {

    const header = document.querySelector('[data-site-header]');
    if (!header) return;

    // El header sube desde justo debajo de la barra de promociones hasta
    // quedar pegado en top: 0, exactamente al ritmo del scroll, y una vez
    // alcanzado ese punto se queda fijo ahí para el resto de la página.
    let promoBarHeight = 0;

    const readPromoBarHeight = () => {
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue('--promo-bar-height');
      const value = parseFloat(raw);
      promoBarHeight = Number.isNaN(value) ? 0 : value;
    };

    let ticking = false;

    const update = () => {
      ticking = false;
      const y = window.scrollY || window.pageYOffset;
      const offset = Math.max(promoBarHeight - y, 0);
      header.style.transform = offset > 0 ? `translateY(${offset}px)` : '';
    };

    const request = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    const init = () => {
      readPromoBarHeight();
      update();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', () => {
      readPromoBarHeight();
      request();
    });
  };

  /* === 2. Overlay header turns solid ============================= */
  const initOverlay = () => {

    const header = document.querySelector('[data-site-header]');

    if (!header || !header.classList.contains('site-header--overlay')) return;

    const setup = () => {

      // El header se vuelve sólido justo cuando el hero (fijo) queda
      // completamente tapado por la sección siguiente, no con cualquier
      // scroll mínimo. Para eso observamos el "hero-spacer": en el instante
      // en que su borde inferior pasa por debajo del propio header, la foto
      // del hero ya no se ve detrás y toca ponerse blanco.
      const spacer = document.querySelector('.hero-spacer');

      if (!spacer) {
        // Fallback por si esta plantilla no tiene el hero con reveal:
        // comportamiento anterior basado en la posición sticky del header.
        let ticking = false;

        const update = () => {
          ticking = false;
          const y = window.scrollY || window.pageYOffset;
          let solid = false;
          if (y > 8) {
            const stickyTop = parseFloat(getComputedStyle(header).top);
            const top = header.getBoundingClientRect().top;
            solid = !Number.isNaN(stickyTop) && Math.abs(top - stickyTop) <= 1;
          }
          header.classList.toggle('is-solid', solid);
        };

        const request = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(update);
        };

        update();
        window.addEventListener('scroll', request, { passive: true });
        window.addEventListener('resize', request);
        return;
      }

      const headerHeight = header.offsetHeight || 64;

      const observer = new IntersectionObserver(
        ([entry]) => {
          header.classList.toggle('is-solid', !entry.isIntersecting);
        },
        {
          root: null,
          rootMargin: `-${headerHeight}px 0px 0px 0px`,
          threshold: 0,
        }
      );

      observer.observe(spacer);

    };

    // El header se renderiza ANTES que el hero en el HTML de la página,
    // así que si ejecutamos esto de inmediato, el hero-spacer todavía no
    // existe en el DOM. Esperamos a que el documento esté completamente
    // parseado para buscarlo.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setup);
    } else {
      setup();
    }
  };

  /* === 3. Desktop Shop dropdown ================================== */
  const initDropdown = () => {

    const header = document.querySelector('[data-site-header]');
    const trigger = header && header.querySelector('[data-shop-trigger]');
    const menu = header && header.querySelector('[data-shop-menu]');

    if (!trigger || !menu) return;

    const desktop = window.matchMedia('(min-width: 768px)');
    let timer = null;
    let skipFocus = false;

    const isOpen = () => header.classList.contains('is-menu-open');

    const open = () => {
      clearTimeout(timer);
      if (!desktop.matches) return;

      header.classList.add('is-menu-open');
      trigger.setAttribute('aria-expanded', 'true');
      menu.setAttribute('aria-hidden', 'false');
    };

    const close = () => {
      clearTimeout(timer);

      header.classList.remove('is-menu-open');
      trigger.setAttribute('aria-expanded', 'false');
      menu.setAttribute('aria-hidden', 'true');
    };

    const closeSoon = () => {
      clearTimeout(timer);
      timer = setTimeout(close, 120);
    };

    trigger.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse') open();
    });

    header.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'mouse') closeSoon();
    });

    header.querySelectorAll('.site-header__nav a, .site-header__logo, .site-header__cart').forEach((el) => {

      if (el === trigger) return;

      el.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse') close();
      });

    });

    trigger.addEventListener('click', (e) => {

      if (desktop.matches && !isOpen()) {
        e.preventDefault();
        open();
      }

    });

    trigger.addEventListener('focus', () => {

      if (skipFocus) return;

      if (trigger.matches(':focus-visible')) {
        open();
      }

    });

    header.addEventListener('focusout', (e) => {

      if (!header.contains(e.relatedTarget)) {
        close();
      }

    });

    document.addEventListener('keydown', (e) => {

      if (e.key !== 'Escape' || !isOpen()) return;

      const focusInside = menu.contains(document.activeElement);

      close();

      if (focusInside) {
        skipFocus = true;
        trigger.focus();
        skipFocus = false;
      }

    });

    document.addEventListener('click', (e) => {

      if (isOpen() && !header.contains(e.target)) {
        close();
      }

    });

    window.addEventListener('scroll', () => {

      if (isOpen()) {
        close();
      }

    }, { passive: true });

    desktop.addEventListener('change', close);
  };

  /* === 4. Mobile menu: Shop submenu ============================== */
  const initMobileSubmenu = () => {

    const menu = document.querySelector('#mobile-menu');

    if (!menu) return;

    const trigger = menu.querySelector('[data-mobile-shop-trigger]');
    const submenu = menu.querySelector('[data-mobile-shop-menu]');

    if (!trigger || !submenu) return;

    trigger.addEventListener('click', () => {

      const isOpen =
        trigger.getAttribute('aria-expanded') === 'true';

      trigger.setAttribute(
        'aria-expanded',
        String(!isOpen)
      );

      submenu.hidden = isOpen;

    });
  };

  const init = () => {
    initOffset();
    initOverlay();
    initDropdown();
    initMobileSubmenu();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
